import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories, categorySlugMap } from "@/mocks/categories";
import { articlesByCategory } from "@/mocks/articles";
import { siteInfo } from "@/mocks/site";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedSlug, setSelectedSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setSelectedSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedSlug]);

  const currentCategory = categorySlugMap[selectedSlug] || categories[0];
  const categoryArticles = articlesByCategory[selectedSlug] || [];

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${currentCategory.name} - ${siteInfo.name}`,
            description: currentCategory.description,
            url: `${siteInfo.url}/category/${selectedSlug}`,
            isPartOf: {
              "@type": "WebSite",
              name: siteInfo.name,
              url: siteInfo.url,
            },
          }),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: "首页", path: "/" },
                { label: currentCategory.name },
              ]}
            />

            <div className="py-6 md:py-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                {currentCategory.name}
              </h1>
              <p className="text-foreground-500 text-sm max-w-2xl">
                {currentCategory.description}
              </p>
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setSelectedSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    selectedSlug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70"
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 pb-14 md:pb-20">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-200"
                  >
                    <div className="relative h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.featured && (
                        <span className="absolute top-3 right-3 px-2 py-0.5 bg-accent-500 text-background-50 text-xs rounded-full whitespace-nowrap">
                          精选
                        </span>
                      )}
                    </div>
                    <div className="p-4 md:p-5">
                      <h2 className="text-base md:text-lg font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span>{article.date}</span>
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-heart-line"></i> {article.likes}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}